<template>
  <v-card id="species-form">
    <v-card-title class="primary white--text">
      <v-row class="pa-2" justify="space-between">
        <span class="headline">
          {{ title }}
        </span>
        <BaseDialogActions hideRefresh hideFullscreen />
      </v-row>
    </v-card-title>

    <v-card-text class="pt-6 pb-4">
      <v-row justify="end">
        <v-col cols="12" md="6">
          <v-card outlined>
            <v-card-title class="subtitle-1">
              {{ $t('chosen') }}
            </v-card-title>
            <v-data-table
            fixed-header
            :height="$vuetify.breakpoint.mdAndUp ? 'max(calc(80vh - 128px), 256px)' : ''"
            hide-default-footer
            class="small-headers"
            :items-per-page="-1"
            :items="chosenProducts"
            :headers="productHeaders"
            dense>
            <template #item.actions="{ item }">
              <v-icon
              small
              color="rgba(0,0,0,0.6)"
              @click="productChosen(item)">
                mdi-close
              </v-icon>
            </template>
            <template #item.isScalable="{ item }">
              <Icon
              v-if="item.isScalable"
              icon="mdi-ruler-square"
              iconColor="secondary"
              :tooltipText="$t('scalable')"/>
            </template>
            </v-data-table>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card outlined>
            <v-card-title class="subtitle-1">
              {{ $t('available') }}
            </v-card-title>
            <v-data-table
            :loading="productsLoading"
            fixed-header
            :height="$vuetify.breakpoint.mdAndUp ? 'max(calc(80vh - 128px), 256px)' : ''"
            hide-default-footer
            class="small-headers"
            :items-per-page="-1"
            :items="availableProducts"
            :headers="productHeaders"
            dense>
            <template #item.actions="{ item }">
              <v-icon
              v-if="isNotFallbackProduct(item)"
              :color="chosenProducts.some(p => p.productId === item.productId) ? 'success' : ''"
              @click="productChosen(item)">
                {{ chosenProducts.some(p => p.productId === item.productId) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
              </v-icon>

              <v-tooltip bottom v-else color="grey">
                <template #activator="{ on }">
                  <v-icon v-on="on">mdi-minus-box</v-icon>
                </template>
                <span class="subtitle-1 white--text">{{ $t('fallbackProduct') }}</span>
              </v-tooltip>

            </template>
            <template #item.isScalable="{ item }">
              <Icon
              v-if="item.isScalable"
              icon="mdi-ruler-square"
              iconColor="secondary"
              :tooltipText="$t('scalable')"/>
            </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="pa-0">
      <v-container fluid class="px-0 pt-0">
        <v-row justify="end">
          <v-col cols="auto">
            <v-btn @click="submitClicked" class="primary white--text">{{ $t('save') }}</v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import { localeCompareAlphanumeric } from '@/utils/base'
import { cloneDeep } from 'lodash'

export default {
  name: 'AccountProductsForm',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    account: {
      type: Object,
      required: true
    }
  },

  mounted () {
    const existingRows = this.account.accountProducts ?? []
    this.chosenProducts = cloneDeep(existingRows.map(row => this.allProducts.find(p => p.productId === row.productId) ?? row))
  },

  beforeDestroy () {
    document.removeEventListener('keyup', this.onKeyup)
  },

  data: () => ({
    chosenProducts: []
  }),

  computed: {
    ...mapGetters('product', ['allProducts', 'productsLoading']),

    title () {
      return `${this.$t('products')} (${this.account.name})`
    },

    headers () {
      return []
    },

    availableProducts () {
      return this.allProducts.toSorted((a, b) => {
        if (this.isNotFallbackProduct(a) && this.isNotFallbackProduct(b)) {
          return localeCompareAlphanumeric(a.name, b.name)
        } else {
          return this.isNotFallbackProduct(a) ? -1 : (this.isNotFallbackProduct(b) ? 1 : 0)
        }
      })
    },

    productHeaders () {
      return [{
        text: '',
        value: 'actions',
        align: 'center',
        width: 80,
        sortable: false
      }, {
        text: this.$t('name'),
        value: 'name'
      }, {
        text: '',
        value: 'isScalable',
        align: 'center',
        width: 48,
        sortable: false
      }]
    }
  },

  methods: {
    productChosen (product) {
      if (this.chosenProducts.every(p => p.productId !== product.productId)) {
        this.chosenProducts.push(product)
      } else {
        this.chosenProducts = this.chosenProducts.filter(p => p.productId !== product.productId)
      }
    },

    submitClicked () {
      this.$emit('products-chosen', this.chosenProducts)
    },

    isNotFallbackProduct (product) {
      return this.account.fallbackProductId !== product.productId
    }
  }
}
</script>
